import React, { useState, useEffect, useCallback, useRef } from "react";
import { Layout, Button, notification, Popconfirm } from "antd";
import { Row, Form, Input, Col, Select, Collapse } from "antd";
import { Link } from "react-router-dom";

import TopHeader from "../../common/TopHeader/TopHeader";
import Sidebar from "../../common/Sidebar/Sidebar";
import Pagination from "../../common/Pagination/Pagination";
import "../../common/Pagination/Pagination.css";

import {
    MinusCircleTwoTone,
    PlusCircleTwoTone,
    DeleteOutlined,
    EditOutlined,
} from "@ant-design/icons";
import QuestionnairesApi from "../../common/Apis/QuestionnairesApi";
import StorageSensorApi from "../../common/Apis/StorageSensorApi";

const StorageSensor = () => {
    const { Content } = Layout;
    const { Panel } = Collapse;

    // ── Data ────────────────────────────────────────────────
    const [Sensors, setSensors] = React.useState([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    // ── Pagination ──────────────────────────────────────────
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [formPaginate] = Form.useForm();
    formPaginate.setFieldsValue({ paginate: pageSize });

    const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));

    // ── Fetch ───────────────────────────────────────────────
    const getSensors = useCallback(() => {
        StorageSensorApi.ListSensors()
            .then((response) => {
                const list = response.payloadDto || [];
                setSensors(list);
                setTotalElements(list.length);
                setTotalPages(Math.ceil(list.length / pageSize));
                setCurrentPage(1);
            })
            .catch((err) => {
                console.log(err);
            });
    }, [pageSize]);

    const [isLoading, setIsLoading] = useState(false);
    const PageLoading = useCallback(() => {
        setIsLoading(true);
        setTimeout(() => setIsLoading(false), 800);
    }, []);

    useEffect(() => {
        PageLoading();
        getSensors();
    }, [PageLoading, getSensors]);

    // ── Pagination handlers ─────────────────────────────────
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    const handlePageSizeChange = (value) => {
        setPageSize(value);
        setCurrentPage(1);
        formPaginate.setFieldsValue({ paginate: value });
        setTotalPages(Math.ceil(Sensors.length / value));
    };

    // ── Filters ─────────────────────────────────────────────
    const formRef = useRef(null);
    const [filterStorage, setFilterStorage] = useState([
        { title: "", answerType: "", name: "" },
    ]);

    const handleAddfilterStorage = () => {
        setFilterStorage([
            ...filterStorage,
            { title: "", answerType: "", name: "" },
        ]);
    };

    const handleRemovefilterStorage = (FiltergetStorageIndex) => {
        const next = [...filterStorage];
        next.splice(FiltergetStorageIndex, 1);
        setFilterStorage(next);

        if (FiltergetStorageIndex === 0) {
            getSensors();   // ← re-fetch unfiltered list
        }
    };

    const handleClear = () => {
        setIsLoading(true);
        getSensors();
        setTimeout(() => setIsLoading(false), 800);
        setFilterStorage([{ title: "", answerType: "", name: "" }]);
    };

    const handleSubmit = (data) => {
        setIsLoading(true);
        setTimeout(() => setIsLoading(false), 800);

        let singleLineString = filterStorage
            .filter((obj) => obj.title && obj.answerType && obj.name)   // ← skip empty rows
            .map((obj) => {
                if (obj.answerType === "%") {
                    return `${obj.title}=${obj.answerType}${obj.name}${obj.answerType}`;
                } else if (obj.answerType === "=") {
                    return `${obj.title}${obj.answerType}${obj.name}`;
                } else {
                    return `${obj.title}=${obj.name}`;
                }
            })
            .join("&");

        let filderData = window.btoa(
            "componentCode=SENSOR&subTenant.id=" +
                decodedToken?.subTenantId +
                "&" +
                singleLineString
        );

        QuestionnairesApi.FilterQuestionsetNext(filderData, 0, pageSize)
            .then((response) => {
                const filtered = response.payloadDto || [];
                setSensors(filtered);
                setTotalElements(filtered.length);
                setTotalPages(Math.ceil(filtered.length / pageSize));
                setCurrentPage(1);            // ← reset to first page
            })
            .catch((err) => {
                console.log(err);
            });
    };

    const handleChangeTitle = (i, value) => {
        const next = [...filterStorage];
        next[i].title = value;
        setFilterStorage(next);
    };
    const handleChangeAnswertype = (i, value) => {
        const next = [...filterStorage];
        next[i].answerType = value;
        setFilterStorage(next);
    };
    const handleChangename = (i, value) => {
        const next = [...filterStorage];
        next[i].name = value;
        setFilterStorage(next);
    };

    const handleDeleteStorage = (SensorID) => {
        StorageSensorApi.DeleteSensors(SensorID)
            .then(() => {
                notification.open({
                    message: <span style={{ fontWeight: "bold" }}>Deleted</span>,
                    description: "Delete Successfully",
                    closeIcon: false,
                    duration: 3,
                    style: { backgroundColor: "#b3ffb3", color: "green" },
                });
                PageLoading();
                getSensors();
            })
            .catch((err) => {
                if (err.apiError?.status) {
                    notification.open({
                        message: <span style={{ fontWeight: "bold" }}>Error</span>,
                        description: err.apiError.message,
                        closeIcon: false,
                        duration: 3,
                        style: { backgroundColor: "#fff2f0", color: "red" },
                    });
                }
            });
    };

    // ── Render ──────────────────────────────────────────────
    const paginatedData = Sensors.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
    );

    const renderTableRows = () => {
        if (paginatedData.length > 0) {
            return paginatedData.map((item) => (
                <tr key={item?.id} className="text-center">
                    <td className="pt-3" style={{ width: "15%" }}>{item?.value}</td>
                    <td className="pt-3" style={{ width: "12%" }}>{item?.severity}</td>
                    <td className="pt-3" style={{ width: "13%" }}>{item?.sensorStatus}</td>
                    <td className="pt-3" style={{ width: "13%" }}>{item?.sensorOrigin}</td>
                    <td className="pt-3" style={{ width: "13%" }}>{item?.storageDTO?.name}</td>
                    <td className="pt-3" style={{ width: "10%" }}>{item?.sensorDTO?.name}</td>
                    <td style={{ width: "20%" }}>
                        <div className="d-flex ">
                            <Link
                                to={`/masters/storage-sensor/edit-storage-sensor?id=${item?.id}`}
                                className={
                                    decodedToken?.permissions?.some(
                                        (permission) =>
                                            permission?.authority === "EDIT_STORAGE_SENSOR"
                                    )
                                        ? "btn btn-primary btn-save table-action icon-center w-100"
                                        : "d-none"
                                }
                            >
                                <EditOutlined />
                                Edit
                            </Link>
                            <Popconfirm
                                title="Are you sure ?"
                                description="Do you really want to delete this Storage Sensor ?"
                                okText="Yes"
                                placement="topRight"
                                onConfirm={() => handleDeleteStorage(item.id)}
                                className="w-100"
                                cancelText="No"
                            >
                                <Link
                                    to={``}
                                    className={
                                        decodedToken?.permissions?.some(
                                            (permission) =>
                                                permission?.authority ===
                                                "DELETE_STORAGE_SENSOR"
                                        )
                                            ? "btn btn-primary btn-back table-action icon-center mx-1"
                                            : "d-none"
                                    }
                                >
                                    <DeleteOutlined />
                                    Delete
                                </Link>
                            </Popconfirm>
                        </div>
                    </td>
                </tr>
            ));
        } else {
            return (
                <tr className="text-center EmptyTable">
                    <td colSpan={7} className="text-center ">
                        <img
                            src="/assest/Images/emptyIcon.svg"
                            className="logo-png mt-3"
                            style={{ width: "10%" }}
                            alt=""
                        />
                        <br />
                        <Button
                            className={
                                decodedToken?.permissions?.some(
                                    (permission) =>
                                        permission?.authority === "ADD_STORAGE_SENSOR"
                                )
                                    ? "btn btn-primary btn-save m-0 my-2 mt-4 "
                                    : "d-none"
                            }
                            href={"/masters/storage-sensor/add-storage-sensor"}
                        >
                            Add Storage Sensor
                        </Button>
                    </td>
                </tr>
            );
        }
    };

    return (
        <Layout style={{ minHeight: "100vh" }}>
            <Sidebar />
            <Layout>
                <TopHeader />
                {isLoading && (
                    <div className="spinner-overlay" id="spinnerOverlay">
                        <div className="spinner"></div>
                    </div>
                )}
                <div style={{ padding: "20px", paddingBottom: "5px" }}>
                    <Form
                        ref={formRef}
                        name="nest-messages"
                        layout="vertical"
                        className="FormClass FilterForm"
                        onFinish={handleSubmit}
                    >
                        <Collapse>
                            <Panel
                                header={
                                    <h5 className="mb-0">
                                        <b>Filter</b>
                                    </h5>
                                }
                                key="1"
                            >
                                {filterStorage.map((input, filterStorageIndex) => (
                                    <div key={filterStorageIndex}>
                                        <Row gutter={12} className="pt-2">
                                            <Col span={5}>
                                                <Form.Item
                                                    name={`title${filterStorageIndex}`}
                                                    className="text-lable"
                                                    rules={[{ required: true, message: "Please Select" }]}
                                                >
                                                    <Select
                                                        className="control-Select"
                                                        placeholder="Filter By"
                                                        value={input.title}
                                                        onChange={(value) =>
                                                            handleChangeTitle(filterStorageIndex, value)
                                                        }
                                                        options={[
                                                            { value: "name", label: "Sensor Name" },
                                                            { value: "storageDTO.name", label: "Storage" },
                                                        ]}
                                                    />
                                                </Form.Item>
                                            </Col>

                                            <Col span={4}>
                                                <Form.Item
                                                    name={`type${filterStorageIndex}`}
                                                    className="text-lable"
                                                    rules={[{ required: true, message: "Please Select type." }]}
                                                >
                                                    <Select
                                                        className="control-Select mb-1"
                                                        placeholder="Condition"
                                                        onChange={(value) =>
                                                            handleChangeAnswertype(filterStorageIndex, value)
                                                        }
                                                        options={[
                                                            { value: "%", label: "Matches with" },
                                                            { value: "=", label: "Equals to" },
                                                        ]}
                                                    />
                                                </Form.Item>
                                            </Col>

                                            <Col span={5}>
                                                <Form.Item
                                                    name={`name${filterStorageIndex}`}
                                                    className="text-lable"
                                                    rules={[{ required: true, message: "Please enter Text." }]}
                                                >
                                                    <Input
                                                        type="text"
                                                        onChange={(e) =>
                                                            handleChangename(filterStorageIndex, e.target.value)
                                                        }
                                                        placeholder="Text"
                                                        className="control-input"
                                                    />
                                                </Form.Item>
                                            </Col>

                                            <Col span={8}>
                                                <Form.Item className="text-lable">
                                                    {filterStorageIndex === 0 ? (
                                                        <Row gutter={23} className="mb-3">
                                                            <Col span={2} className="mx-0">
                                                                <Form.Item className="text-lable">
                                                                    <PlusCircleTwoTone
                                                                        onClick={handleAddfilterStorage}
                                                                    />
                                                                </Form.Item>
                                                            </Col>
                                                            <Col span={10}>
                                                                <Button
                                                                    type="primary"
                                                                    htmlType="submit"
                                                                    className="btn btn-primary btn-save table-action icon-center "
                                                                >
                                                                    Filter
                                                                </Button>
                                                            </Col>
                                                            <Col span={10}>
                                                                <Button
                                                                    type="reset"
                                                                    htmlType="reset"
                                                                    onClick={() => handleClear()}
                                                                    style={{ color: "#0b5ed7" }}
                                                                    className="btn btn-primary btn-back table-action icon-center"
                                                                >
                                                                    Clear
                                                                </Button>
                                                            </Col>
                                                        </Row>
                                                    ) : (
                                                        <MinusCircleTwoTone
                                                            onClick={() =>
                                                                handleRemovefilterStorage(filterStorageIndex)
                                                            }
                                                        />
                                                    )}
                                                </Form.Item>
                                            </Col>
                                        </Row>
                                    </div>
                                ))}
                            </Panel>
                        </Collapse>
                    </Form>
                </div>

                <Content
                    className="form-content nav-content"
                    style={{ margin: "14px 16px 0", padding: "30px", background: "white" }}
                >
                    <div>
                        {/* Toolbar */}
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            {paginatedData?.length > 0 && (
                                <Button
                                    className={
                                        decodedToken?.permissions?.some(
                                            (permission) =>
                                                permission?.authority === "ADD_STORAGE_SENSOR"
                                        )
                                            ? "btn btn-primary btn-save m-0 my-2"
                                            : "d-none"
                                    }
                                    href={"/masters/storage-sensor/add-storage-sensor"}
                                >
                                    Add Storage Sensor
                                </Button>
                            )}
                            <Form form={formPaginate}>
                                <Form.Item
                                    name="paginate"
                                    className="text-lable"
                                    style={{ width: "100px", marginBottom: 0 }}
                                >
                                    <Select
                                        className="control-Select"
                                        onChange={(value) => handlePageSizeChange(value)}
                                        options={[
                                            { value: 10, label: "10" },
                                            { value: 20, label: "20" },
                                            { value: 30, label: "30" },
                                            { value: 40, label: "40" },
                                            { value: 50, label: "50" },
                                            { value: 100, label: "100" },
                                        ]}
                                    />
                                </Form.Item>
                            </Form>
                        </div>

                        <table className="table users-table">
                            <thead>
                                <tr>
                                    <th>Value</th>
                                    <th>Severity</th>
                                    <th>Sensor Status</th>
                                    <th>Sensor Origin</th>
                                    <th>Storage</th>
                                    <th>Sensor</th>
                                    <th className="text-center">Action</th>
                                </tr>
                            </thead>
                            <tbody>{renderTableRows()}</tbody>
                        </table>

                        {paginatedData.length > 0 && (
                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                                maxVisiblePages={5}
                                showPageInfo={true}
                                totalItems={totalElements}
                                pageSize={pageSize}
                            />
                        )}
                    </div>
                </Content>
            </Layout>
        </Layout>
    );
};

export default StorageSensor;